import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import DateSelect from '../components/ui/DateSelect';
import SearchableSelect from '../components/ui/SearchableSelect';
import { useStudentStore } from '../store/StudentStore';
import StaffService from '../services/StaffService';
import { formatDateTime } from '../utils/dateFormat';

const getAttachmentUrl = (doc) => doc?.fileUrl || doc?.url || doc?.metadata?.fileUrl || '';

const openAttachment = (doc) => {
  const url = getAttachmentUrl(doc);
  if (!url) return;
  window.open(url, '_blank', 'noopener,noreferrer');
};

const downloadAttachment = (doc) => {
  const url = getAttachmentUrl(doc);
  if (!url) return;
  const link = document.createElement('a');
  link.href = url;
  link.download = doc?.name || 'attachment';
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export default function IncidentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { incidentController, incidents, students, inventoryItems } = useStudentStore();
  const staffService = useMemo(() => new StaffService(), []);
  const staffList = useMemo(() => staffService.list(), [staffService]);
  const incident = incidents.find((item) => item.id === id) || incidentController.getIncident(id);

  const staffById = useMemo(() => {
    return staffList.reduce((acc, staff) => {
      acc[staff.id] = staff;
      return acc;
    }, {});
  }, [staffList]);
  const staffOptions = useMemo(
    () =>
      staffList.map((staff) => ({
        value: staff.name,
        label: staff.name,
        meta: staff.role || staff.title || ''
      })),
    [staffList]
  );

  const studentById = useMemo(() => {
    return students.reduce((acc, student) => {
      acc[student.id] = student;
      return acc;
    }, {});
  }, [students]);

  const status = incident?.status || 'open';
  const [assignedKeyWorker, setAssignedKeyWorker] = useState(incident?.assignedKeyWorker || '');
  const [followUpRequired, setFollowUpRequired] = useState(Boolean(incident?.followUpRequired));
  const [followUpDate, setFollowUpDate] = useState(incident?.followUpDate || '');
  const [followUpPrompt, setFollowUpPrompt] = useState(incident?.followUpPrompt || '');

  if (!incident) {
    return (
      <Card className="p-6">
        <p className="sp-muted">Incident not found.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/incidents')}>
          Back to incidents
        </Button>
      </Card>
    );
  }

  const studentParticipants = incident.participants.filter((p) => p.type === 'student');
  const staffParticipants = incident.participants.filter((p) => p.type === 'staff');
  const linkedItems = inventoryItems.filter((item) => incident.inventoryItemIds.includes(item.id));

  const handleSave = () => {
    incidentController.updateIncident(
      incident.id,
      {
        status,
        assignedKeyWorker,
        followUpRequired,
        followUpDate: followUpRequired ? followUpDate : null,
        followUpPrompt: followUpRequired ? followUpPrompt : ''
      },
      { name: 'Admin User' }
    );
  };

  const buildForwardPayload = () => {
    return {
      subject: `Incident ${incident.id}: ${incident.type}`,
      body: [
        `Incident: ${incident.type}`,
        `Severity: ${incident.severity}`,
        `Status: ${incident.status}`,
        `Reported: ${formatDateTime(incident.date)}`,
        incident.followUpRequired ? `Follow-up due: ${incident.followUpDate || 'Required'}` : 'Follow-up: None',
        incident.description ? `Notes: ${incident.description}` : ''
      ]
        .filter(Boolean)
        .join('\n'),
      references: [
        {
          type: 'incident',
          id: incident.id,
          label: `Incident ${incident.id}`,
          route: `/incidents/${incident.id}`
        }
      ]
    };
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="sp-value" style={{ fontSize: '20px', fontWeight: 600 }}>Incident {incident.id}</h2>
          <p className="sp-muted">{incident.type} · Reported {formatDateTime(incident.date)}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/incidents')}>Back to incidents</Button>
          <Button
            variant="secondary"
            onClick={() => navigate('/messages', { state: { compose: buildForwardPayload() } })}
          >
            Forward internally
          </Button>
          <Button onClick={handleSave}>Save changes</Button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="sp-panel p-4 md:col-span-2 space-y-4">
          <div className="flex items-center gap-2">
            <Badge variant={incident.severity}>{incident.severity}</Badge>
            <Badge variant={incident.status === 'closed' ? 'inactive' : 'review'}>{incident.status}</Badge>
          </div>
          <div>
            <div className="sp-label">Description</div>
            <p className="text-sm">{incident.description || 'No description provided.'}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="sp-label">Reported By</div>
              <p className="text-sm">{incident.reportedBy || '—'}</p>
            </div>
            <div>
              <div className="sp-label">Assigned Key Worker</div>
              <SearchableSelect
                value={assignedKeyWorker}
                options={[{ value: '', label: 'Unassigned' }, ...staffOptions]}
                placeholder="Search staff"
                emptyLabel="No staff found."
                ariaLabel="Search key worker"
                className="mt-2"
                onChange={(value) => setAssignedKeyWorker(value)}
              />
            </div>
          </div>
          <div>
            <div className="sp-label">Follow-up</div>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={followUpRequired}
                  onChange={(e) => setFollowUpRequired(e.target.checked)}
                />
                Follow-up required
              </label>
              {followUpRequired && (
                <DateSelect value={followUpDate} onChange={setFollowUpDate} />
              )}
            </div>
            {followUpRequired && (
              <textarea
                className="sp-textarea w-full mt-2"
                rows={3}
                value={followUpPrompt}
                onChange={(e) => setFollowUpPrompt(e.target.value)}
                placeholder="Follow-up guidance"
              />
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="sp-panel p-4">
            <div className="sp-label">Students involved</div>
            <div className="mt-2 space-y-2">
              {studentParticipants.length === 0 && <p className="text-sm sp-muted">None recorded.</p>}
              {studentParticipants.map((student) => (
                <button
                  key={student.id}
                  type="button"
                  className="sp-button sp-button--ghost w-full text-left"
                  onClick={() => navigate(`/students/${student.id}/dashboard`)}
                >
                  {studentById[student.id]?.name || student.name || student.id}
                </button>
              ))}
            </div>
          </div>

          <div className="sp-panel p-4">
            <div className="sp-label">Staff involved</div>
            <div className="mt-2 space-y-2">
              {staffParticipants.length === 0 && <p className="text-sm sp-muted">None recorded.</p>}
              {staffParticipants.map((staff) => (
                <div key={staff.id} className="text-sm">
                  {staffById[staff.id]?.name || staff.name || staff.id}
                </div>
              ))}
            </div>
          </div>

          <div className="sp-panel p-4">
            <div className="sp-label">Inventory linked</div>
            <div className="mt-2 space-y-2">
              {linkedItems.length === 0 && <p className="text-sm sp-muted">No inventory linked.</p>}
              {linkedItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="sp-button sp-button--ghost w-full text-left"
                  onClick={() => navigate(`/inventory/${item.id}`)}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>

          <div className="sp-panel p-4">
            <div className="sp-label">Attachments</div>
            <div className="mt-2 space-y-2">
              {incident.documents.length === 0 && <p className="text-sm sp-muted">No attachments.</p>}
              {incident.documents.map((doc) => (
                <div key={doc.id || doc.name} className="sp-panel sp-panel--muted p-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <span className="text-sm">{doc.name}</span>
                    <div className="flex items-center gap-2">
                      {getAttachmentUrl(doc) && (
                        <Button size="sm" variant="secondary" type="button" onClick={() => openAttachment(doc)}>
                          View
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        type="button"
                        onClick={() => downloadAttachment(doc)}
                        disabled={!getAttachmentUrl(doc)}
                      >
                        Download
                      </Button>
                    </div>
                  </div>
                  {!getAttachmentUrl(doc) && (
                    <div className="text-xs sp-muted mt-1">No file data available.</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
